/**
 * ContentAuditDetail — the AI Content Discoverability™ report.
 *
 * The report reads as a client-facing strategic diagnostic (Joan, 2026-08-22),
 * not a technical site audit: it opens with what is working, what is limiting AI
 * discoverability, why that matters and what to do first; groups the scorecard
 * into technical vs content discoverability; states plainly what AI can and
 * cannot determine from the site; and lands on at most five priority actions.
 *
 * SECTION ORDER comes from `contentReportSections()` in @aurora/shared — the same
 * function the Markdown export calls, so page and document cannot drift on which
 * sections exist, what they are called or how they are numbered. The same is true
 * of the shared copy blocks and of the fallbacks that keep a report stored before
 * the rework rendering correctly (every new field is optional and guarded).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CONTENT_METHODOLOGY_COPY,
  comprehensibilityRows,
  contentAdditionalOpportunities,
  contentExecutiveQuestions,
  contentFrictionByGroup,
  contentPriorityActions,
  contentReportSections,
  type ContentAudit,
  type ContentAuditPage,
  type ContentAuditReport,
  type ContentFrictionRow,
  type ContentSignalStatus,
} from '@aurora/shared';
import { api, ApiClientError } from '../api/client';
import { ContentScorecard } from '../components/ContentScorecard';
import { contentPillClass } from './ContentAudits';

const RUNNING_STATUSES = new Set(['pending', 'crawling', 'analyzing']);

const STATUS_MESSAGE: Record<string, string> = {
  pending: 'Queued…',
  crawling: 'Crawling the site (robots.txt, sitemap, key pages)…',
  analyzing: 'Analyzing content structure and running the auditor…',
};

function Section({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="audit-section">
      <h2>
        {n}. {title}
      </h2>
      {children}
    </section>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element | null {
  if (!value || !value.trim()) return null;
  return (
    <p>
      <strong>{label}:</strong> {value}
    </p>
  );
}

function flagPill(s: ContentSignalStatus): JSX.Element {
  const cls =
    s === 'fail'
      ? 'pill pill-failed'
      : s === 'warn'
        ? 'pill pill-pending'
        : s === 'pass'
          ? 'pill pill-complete'
          : 'pill pill-processing';
  return <span className={cls}>{s}</span>;
}

function severityPill(s: string): JSX.Element {
  const cls =
    s === 'High'
      ? 'pill pill-failed'
      : s === 'Low'
        ? 'pill pill-complete'
        : 'pill pill-pending';
  return <span className={cls}>{s}</span>;
}

function ratingPill(s: string | null): JSX.Element {
  if (!s) return <span className="muted">—</span>;
  const cls =
    s === 'Weak'
      ? 'pill pill-failed'
      : s === 'Strong'
        ? 'pill pill-complete'
        : 'pill pill-pending';
  return <span className={cls}>{s}</span>;
}

/** Joan §5 — every finding paired with its hedged potential AI impact. */
function FrictionTable({
  rows,
}: {
  rows: ContentFrictionRow[];
}): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <div className="roadmap-phase">
      <h3>Finding → Potential AI Impact</h3>
      <table className="leads-table">
        <thead>
          <tr>
            <th>Finding</th>
            <th>Potential AI impact</th>
            <th>Severity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td>{row.issue}</td>
              <td>{row.impact}</td>
              <td>{severityPill(row.severity)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExecutiveSummary({
  report,
}: {
  report: ContentAuditReport;
}): JSX.Element {
  const lead =
    report.executiveSummary.narrative ||
    report.executiveSummary.overallAssessment;
  return (
    <>
      {lead && <p>{lead}</p>}
      {contentExecutiveQuestions(report).map((q) => (
        <Field key={q.label} label={q.label} value={q.value} />
      ))}
    </>
  );
}

function BenchmarkLinkage({
  report,
}: {
  report: ContentAuditReport;
}): JSX.Element {
  const d = report.benchmarkLinkageDetail;
  if (!d) return <p>{report.benchmarkLinkage}</p>;
  const figures = [
    d.visibilityRate !== null ? `visibility ${pct(d.visibilityRate)}` : null,
    d.citationRate !== null ? `citation ${pct(d.citationRate)}` : null,
  ].filter((x): x is string => x !== null);
  return (
    <>
      {figures.length > 0 && (
        <p>
          <strong>Benchmark performance ({d.company}):</strong>{' '}
          {figures.join(', ')} across the linked AI Discoverability Benchmark
          run.
        </p>
      )}
      {d.summary && <p>{d.summary}</p>}
      {d.contributingFactors.length > 0 && (
        <>
          <h3>Website factors that may be contributing</h3>
          <ul>
            {d.contributingFactors.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </>
      )}
      <p className="muted small">{d.caveat}</p>
    </>
  );
}

function TechnicalFindings({
  report,
}: {
  report: ContentAuditReport;
}): JSX.Element {
  const repeated = report.repeatedIssues ?? [];
  return (
    <>
      {report.crawlAccess.summary && <p>{report.crawlAccess.summary}</p>}
      <h3>AI crawler access</h3>
      <p className="section-note">
        Split by what each crawler is FOR (2026-09-01) — blocking a{' '}
        <strong>retrieval</strong> crawler makes the site invisible to AI
        answers right now; blocking a <strong>training</strong> crawler only
        affects future model versions and has no effect on what an
        already-trained model can answer today. A site can have one open and
        the other blocked at the same time.
      </p>
      <h4>Retrieval — fetches the site live to answer a question now</h4>
      <table className="leads-table">
        <thead>
          <tr>
            <th>AI Crawler</th>
            <th>Access</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {report.crawlAccess.aiCrawlers
            .filter((c) => c.purpose === 'retrieval')
            .map((c) => (
              <tr key={c.bot}>
                <td className="lead-name">{c.bot}</td>
                <td>
                  {c.allowed === true ? (
                    <span className="pill pill-complete">Allowed</span>
                  ) : c.allowed === false ? (
                    <span className="pill pill-failed">Blocked</span>
                  ) : (
                    <span className="pill pill-processing">Unknown</span>
                  )}
                </td>
                <td>{c.note}</td>
              </tr>
            ))}
        </tbody>
      </table>
      <h4>Training — builds a future model's knowledge, not today's answers</h4>
      <table className="leads-table">
        <thead>
          <tr>
            <th>AI Crawler</th>
            <th>Access</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {report.crawlAccess.aiCrawlers
            .filter((c) => c.purpose === 'training')
            .map((c) => (
              <tr key={c.bot}>
                <td className="lead-name">{c.bot}</td>
                <td>
                  {c.allowed === true ? (
                    <span className="pill pill-complete">Allowed</span>
                  ) : c.allowed === false ? (
                    <span className="pill pill-failed">Blocked</span>
                  ) : (
                    <span className="pill pill-processing">Unknown</span>
                  )}
                </td>
                <td>{c.note}</td>
              </tr>
            ))}
        </tbody>
      </table>
      <Field label="llms.txt" value={report.crawlAccess.llmsTxt} />
      <Field label="Sitemap" value={report.crawlAccess.sitemap} />
      <Field label="Rendering" value={report.crawlAccess.rendering} />

      <h3>Content structure</h3>
      {report.structureFindings.summary && (
        <p>{report.structureFindings.summary}</p>
      )}
      {report.structureFindings.points.length > 0 && (
        <ul>
          {report.structureFindings.points.map((pt, i) => (
            <li key={i}>{pt}</li>
          ))}
        </ul>
      )}

      {repeated.length > 0 && (
        <div className="roadmap-phase">
          <h3>Technical issues across the audited pages</h3>
          <p className="muted small">
            Each issue is counted once, with the number of audited pages it was
            observed on — rather than repeated on every page row.
          </p>
          <table className="leads-table">
            <thead>
              <tr>
                <th>Issue</th>
                <th>Pages affected</th>
                <th>Status</th>
                <th>Why it matters</th>
              </tr>
            </thead>
            <tbody>
              {repeated.map((i) => (
                <tr key={i.key}>
                  <td>{i.label}</td>
                  <td>
                    {i.pagesAffected} of {i.pagesAudited}
                  </td>
                  <td>{flagPill(i.status)}</td>
                  <td className="small">{i.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FrictionTable rows={contentFrictionByGroup(report).technical} />
    </>
  );
}

function ContentFindings({
  report,
}: {
  report: ContentAuditReport;
}): JSX.Element {
  const rows = comprehensibilityRows(report.comprehensibilityFindings);
  const examples = report.contentExamples ?? [];
  return (
    <>
      {report.comprehensibilityFindings.summary && (
        <p>{report.comprehensibilityFindings.summary}</p>
      )}
      {rows.length > 0 && (
        <table className="leads-table">
          <thead>
            <tr>
              <th>Dimension</th>
              <th>Assessment</th>
              <th>What the content shows</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td className="lead-name">{row.label}</td>
                <td>{ratingPill(row.rating)}</td>
                <td>{row.finding}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <FrictionTable rows={contentFrictionByGroup(report).content} />
      {examples.length > 0 && (
        <div className="roadmap-phase">
          <h3>What more AI-discoverable content looks like</h3>
          {examples.map((ex, i) => (
            <div key={i}>
              {ex.context && (
                <p>
                  <strong>{ex.context}</strong>
                </p>
              )}
              <Field label="Current approach" value={ex.currentApproach} />
              <Field
                label="More AI-discoverable approach"
                value={ex.improvedApproach}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Joan §8 — one row per page carrying its single most important CONTENT
 * opportunity. The technical warnings that used to fill this table are counted
 * once in the Technical Discoverability section instead.
 */
function PageFindings({ report }: { report: ContentAuditReport }): JSX.Element {
  return (
    <>
      <p className="muted small">
        The single most useful content change for each page reviewed. Technical
        issues shared across pages are counted once above rather than repeated
        here.
      </p>
      <table className="leads-table">
        <thead>
          <tr>
            <th>Page</th>
            <th>Primary discoverability opportunity</th>
          </tr>
        </thead>
        <tbody>
          {report.pageAudit.map((row, i) => (
            <tr key={i}>
              <td className="small">{row.url}</td>
              <td>{row.primaryOpportunity ?? row.topIssue}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/** Joan §10 — at most five actions, ordered, each with its rationale. */
function PriorityActions({
  report,
}: {
  report: ContentAuditReport;
}): JSX.Element {
  const actions = contentPriorityActions(report);
  return (
    <>
      <p className="muted small">
        Start with these. They are ordered by how quickly they can be done, how
        much resource they need, and how much they add to what an AI system can
        understand — improving existing pages before creating new content.
      </p>
      {actions.map((a, i) => (
        <div key={i} className="roadmap-phase">
          <h3>
            {i + 1}. {a.title}
          </h3>
          {a.action && a.action !== a.title && <p>{a.action}</p>}
          {a.appliesTo && <Field label="Where" value={a.appliesTo} />}
          <p className="muted small">
            Effort: {a.effort} · Potential impact: {a.impact} · {a.dimension}
          </p>
          <Field label="Why it matters" value={a.whyItMatters} />
        </div>
      ))}
    </>
  );
}

function Report({ report }: { report: ContentAuditReport }): JSX.Element {
  return (
    <div className="audit-report">
      {contentReportSections(report).map((section) => (
        <Section key={section.key} n={section.n} title={section.title}>
          {section.key === 'executiveSummary' && (
            <ExecutiveSummary report={report} />
          )}
          {section.key === 'scorecard' && (
            <ContentScorecard scorecard={report.scorecard} />
          )}
          {section.key === 'understandsToday' && (
            <>
              <p className="muted small">
                What an AI system can work out about the company from the pages
                reviewed.
              </p>
              <ul>
                {(report.aiUnderstanding?.understandsToday ?? []).map(
                  (x, i) => (
                    <li key={i}>{x}</li>
                  ),
                )}
              </ul>
            </>
          )}
          {section.key === 'aiMissing' && (
            <>
              <p className="muted small">
                Information a researcher or buyer would need that the pages
                reviewed do not state clearly enough for an AI system to
                determine.
              </p>
              <ul>
                {(report.aiUnderstanding?.cannotDetermine ?? []).map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </>
          )}
          {section.key === 'benchmarkLinkage' && (
            <BenchmarkLinkage report={report} />
          )}
          {section.key === 'technicalFindings' && (
            <TechnicalFindings report={report} />
          )}
          {section.key === 'contentFindings' && (
            <ContentFindings report={report} />
          )}
          {section.key === 'pageFindings' && <PageFindings report={report} />}
          {section.key === 'contentGaps' && (
            <>
              <p className="muted small">
                Where existing content is already strong, and where more context
                would help AI systems understand the offering.
              </p>
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Content area</th>
                    <th>Current strength</th>
                    <th>Opportunity</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.contentGaps ?? []).map((g, i) => (
                    <tr key={i}>
                      <td className="lead-name">{g.area}</td>
                      <td>{ratingPill(g.currentStrength)}</td>
                      <td>{g.opportunity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {section.key === 'priorityActions' && (
            <PriorityActions report={report} />
          )}
          {section.key === 'additionalOpportunities' && (
            <>
              <p className="muted small">
                Worthwhile improvements that are secondary to the actions above
                — pick these up once the priority actions are underway.
              </p>
              <ul>
                {contentAdditionalOpportunities(report).map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </>
          )}
          {section.key === 'longerTerm' && (
            <>
              {report.longerTermStrategy?.narrative && (
                <p>{report.longerTermStrategy.narrative}</p>
              )}
              {(report.longerTermStrategy?.researcherQuestions.length ?? 0) >
                0 && (
                <>
                  <p className="muted small">
                    Over time, the content should be able to answer questions
                    such as:
                  </p>
                  <ul>
                    {(report.longerTermStrategy?.researcherQuestions ?? []).map(
                      (q, i) => (
                        <li key={i}>{q}</li>
                      ),
                    )}
                  </ul>
                </>
              )}
            </>
          )}
          {section.key === 'methodology' && (
            <>
              {CONTENT_METHODOLOGY_COPY.map((block) => (
                <Field
                  key={block.heading}
                  label={block.heading}
                  value={block.body}
                />
              ))}
            </>
          )}
          {section.key === 'auroraInsight' && <p>{report.auroraInsight}</p>}
        </Section>
      ))}
    </div>
  );
}

function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function SignalsPanel({ auditId }: { auditId: string }): JSX.Element {
  const [pages, setPages] = useState<ContentAuditPage[] | null>(null);
  const [open, setOpen] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const res = await api.getContentAuditPages(auditId);
      setPages(res.items);
    } catch {
      setPages([]);
    }
  };

  const toggle = (): void => {
    const next = !open;
    setOpen(next);
    if (next && pages === null) void load();
  };

  return (
    <section className="audit-section evidence-panel">
      <button type="button" className="btn btn-ghost" onClick={toggle}>
        {open ? 'Hide' : 'Show'} signals (show our work)
      </button>
      {open && (
        <div>
          {pages === null ? (
            <p className="muted">Loading signals…</p>
          ) : pages.length === 0 ? (
            <p className="muted">No per-page signals were recorded.</p>
          ) : (
            pages.map((p) => (
              <div key={p.id} className="signal-page">
                <h4 className="small">
                  {p.url}{' '}
                  <span className="muted">
                    [{p.renderingMode}
                    {p.error ? ` · ${p.error}` : ''}]
                  </span>
                </h4>
                {p.signals.length > 0 && (
                  <table className="leads-table">
                    <thead>
                      <tr>
                        <th>Signal</th>
                        <th>Dimension</th>
                        <th>Status</th>
                        <th>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.signals.map((s, i) => (
                        <tr key={i}>
                          <td>{s.label}</td>
                          <td>{s.dimension}</td>
                          <td>{flagPill(s.status)}</td>
                          <td className="small">{s.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </section>
  );
}

/**
 * `.html` is the formatted client deliverable — branded, set in Poppins, and
 * print-to-PDF from the browser. `.md` remains for editing and pasting.
 */
type ExportFormat = 'pdf' | 'html' | 'md';

export function ContentAuditDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [run, setRun] = useState<ContentAudit | null>(null);
  const [report, setReport] = useState<ContentAuditReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  // Kept apart from `error`: that one replaces the whole page, and a failed
  // download must not take the report off the screen.
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const detail = await api.getContentAudit(id);
      setRun(detail.run);
      setReport(detail.report);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to load audit',
      );
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!run || !RUNNING_STATUSES.has(run.status)) return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [run, load]);

  const onExport = async (format: ExportFormat): Promise<void> => {
    if (!id || !run) return;
    setExporting(format);
    setExportError(null);
    try {
      const res =
        format === 'pdf'
          ? await api.downloadContentAuditPdf(id)
          : format === 'html'
            ? await api.downloadContentAuditHtml(id)
            : await api.downloadContentAuditMarkdown(id);
      if (!res.ok) {
        // A raw download carries the JSON error envelope on failure (e.g. 503
        // when the server has no browser to print with). Show its message
        // instead of saving the JSON as a broken file.
        const body = (await res.json().catch(() => null)) as {
          error?: { message?: string };
        } | null;
        setExportError(
          body?.error?.message ??
            `Export failed (${res.status}). Please try again.`,
        );
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${run.companyName.replace(/\s+/g, '-').toLowerCase()}-content-discoverability.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(null);
    }
  };

  if (error) return <div className="form-error">{error}</div>;
  if (!run) return <div className="muted centered">Loading audit…</div>;

  const running = RUNNING_STATUSES.has(run.status);

  return (
    <div className="audit-detail">
      <div className="page-head">
        <div>
          <Link to="/content-audits" className="back-link">
            ← All audits
          </Link>
          <h1>{run.companyName} — AI Content Discoverability™</h1>
          <div className="audit-meta muted">
            <span className={contentPillClass(run.status)}>{run.status}</span>
            <span> · {run.domain}</span>
            {run.benchmarkRunId && <span> · Benchmark-linked</span>}
            {run.usage && <span> · ${run.usage.cost.toFixed(2)}</span>}
          </div>
        </div>
        {report && (
          <div className="export-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void onExport('pdf')}
              disabled={exporting !== null}
              title="The branded report as a PDF — the client deliverable. Takes a few seconds to print."
            >
              {exporting === 'pdf' ? 'Preparing…' : 'Download report (PDF)'}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void onExport('html')}
              disabled={exporting !== null}
              title="The same branded report as a self-contained HTML file — open it and use Print → Save as PDF"
            >
              {exporting === 'html' ? 'Exporting…' : 'HTML (.html)'}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void onExport('md')}
              disabled={exporting !== null}
              title="Plain Markdown, for editing or pasting elsewhere"
            >
              {exporting === 'md' ? 'Exporting…' : 'Markdown (.md)'}
            </button>
            {exportError && (
              <span className="form-error" role="alert">
                {exportError}
              </span>
            )}
          </div>
        )}
      </div>

      {run.status === 'failed' && (
        <div className="form-error">
          Audit failed: {run.error ?? 'unknown error'}
        </div>
      )}

      {run.warnings.length > 0 && (
        <details className="audit-warnings">
          <summary className="muted">
            {run.warnings.length} note(s) from this run
          </summary>
          <ul>
            {run.warnings.map((w, i) => (
              <li key={i} className="small muted">
                {w}
              </li>
            ))}
          </ul>
        </details>
      )}

      {running && (
        <div className="muted centered">
          {STATUS_MESSAGE[run.status] ?? 'Working…'} (auto-refreshing)
        </div>
      )}

      {report ? (
        <>
          <Report report={report} />
          <SignalsPanel auditId={run.id} />
        </>
      ) : (
        !running && (
          <div className="empty-state muted">No report available.</div>
        )
      )}

      <p className="footer muted small">
        Aurora AI Content Discoverability™ · first-party public content only ·
        Accessibility &amp; Structure are verified facts, Comprehensibility is a
        directional judgment · advisory — Aurora never changes your site.
      </p>
    </div>
  );
}
